import React, { useMemo } from "react"
import "./EmotionBarcode.css"

const EMOTION_COLORS = {
  happy: "#E7C239", amused: "#D93D57", involved: "#E34327", inspired: "#EC9965",
  tender: "#E65C9E", surprise: "#53A7BD", astonished: "#59A8C9", sad: "#3374B4",
  melancholic: "#3374B4", bored: "#95B6D8", compassioned: "#A8D4EA", fear: "#5BAF61",
  disturbed: "#418266", scared: "#428249", disgust: "#7556A5", disappointed: "#9678B6",
  embarrassed: "#3374B4", angry: "#975767", annoyed: "#D096A2", nothing: "#333333",
}

// emotions that read as "related" to each dominant one (by feeling + hue)
const FAMILIES = {
  happy: ["happy", "amused", "inspired", "tender", "astonished"],
  amused: ["amused", "happy", "astonished", "surprise", "inspired"],
  involved: ["involved", "inspired", "surprise", "astonished"],
  inspired: ["inspired", "involved", "happy", "astonished", "surprise"],
  tender: ["tender", "happy", "compassioned", "amused"],
  surprise: ["surprise", "astonished", "involved", "amused"],
  astonished: ["astonished", "surprise", "amused", "inspired"],
  sad: ["sad", "melancholic", "disappointed", "compassioned", "bored"],
  melancholic: ["melancholic", "sad", "bored", "disappointed"],
  bored: ["bored", "melancholic", "disappointed", "sad"],
  compassioned: ["compassioned", "tender", "sad", "melancholic"],
  fear: ["fear", "scared", "disturbed", "surprise"],
  disturbed: ["disturbed", "fear", "disgust", "scared"],
  scared: ["scared", "fear", "disturbed", "surprise"],
  disgust: ["disgust", "disturbed", "disappointed", "angry"],
  disappointed: ["disappointed", "sad", "disgust", "bored"],
  embarrassed: ["embarrassed", "sad", "disappointed", "annoyed"],
  angry: ["angry", "annoyed", "disgust", "disturbed"],
  annoyed: ["annoyed", "angry", "disappointed", "disgust"],
  nothing: ["nothing", "bored", "melancholic"],
}

// tiny deterministic PRNG so a movie's barcode is stable across renders
function seedFromString(s) {
  let h = 1779033703 ^ String(s).length
  for (let i = 0; i < String(s).length; i++) {
    h = Math.imul(h ^ String(s).charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  return h >>> 0
}
function mulberry32(a) {
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const TOTAL = 52 // colored bars before the sparse tail

// Build the bar list. If a real scene summary exists, honour its proportions;
// otherwise generate a stable mix led by the dominant emotion's family.
function buildBars(emotion, summary, seed) {
  const rand = mulberry32(seedFromString(seed || emotion || "x"))
  const dominant = emotion && EMOTION_COLORS[emotion] ? emotion : "nothing"
  const bars = []

  const hasSummary = Array.isArray(summary) && summary.length > 0
  if (hasSummary) {
    // proportional: each emotion gets its % share of bars, dominant first
    summary.forEach((entry) => {
      const count = Math.max(1, Math.round((entry.percentage / 100) * TOTAL))
      for (let i = 0; i < count; i++) bars.push(entry.emotion)
    })
  } else {
    const fam = FAMILIES[dominant] || [dominant]
    for (let i = 0; i < TOTAL; i++) {
      const t = i / TOTAL
      // front-load the dominant emotion, let the family bleed in later
      if (t < 0.32 || rand() < 0.45) {
        bars.push(dominant)
      } else {
        const pickIdx = 1 + Math.floor(rand() * (fam.length - 1 || 1))
        bars.push(fam[Math.min(pickIdx, fam.length - 1)])
      }
    }
  }

  // widths taper from wide (left, dominant) to thin (right) — like the reference
  return bars.slice(0, TOTAL).map((emo, i) => {
    const t = i / TOTAL
    const weight = Math.pow(1 - t, 1.4) * 3 + 0.4 + rand() * 0.3
    return { emotion: emo, color: EMOTION_COLORS[emo] || "#333", weight }
  })
}

export default function EmotionBarcode({ emotion, summary, seed, label }) {
  const bars = useMemo(() => buildBars(emotion, summary, seed), [emotion, summary, seed])

  return (
    <div className="ebc">
      {label && <span className="ebc__label">{label}</span>}
      <div className="ebc__strip">
        {bars.map((b, i) => (
          <span
            key={i}
            className="ebc__bar"
            style={{ flexGrow: b.weight, background: b.color }}
            title={b.emotion}
          />
        ))}
        {/* sparse, stippled tail like the reference palettes */}
        <span className="ebc__tail" />
      </div>
    </div>
  )
}
